import React, { useState } from 'react';
import { ViewType } from '../types';

interface ViewTypeSelectionModalProps {
  availableViewTypes: ViewType[];
  onViewTypeSelected: (viewType: ViewType) => void;
  onCancel: () => void;
}

const ViewTypeSelectionModal: React.FC<ViewTypeSelectionModalProps> = ({
  availableViewTypes,
  onViewTypeSelected,
  onCancel
}) => {
  const [selectedViewType, setSelectedViewType] = useState<ViewType | null>(null);

  const viewTypeLabels: Record<ViewType, { label: string; description: string }> = {
    'product': {
      label: 'Product Name',
      description: 'Group and review keywords by individual product names'
    },
    'brand': {
      label: 'Brand',
      description: 'Group and review keywords by brand names'
    },
    'sub-brand': {
      label: 'Sub-Brand',
      description: 'Group and review keywords by sub-brand names'
    }
  };

  const handleConfirm = () => {
    if (selectedViewType) {
      onViewTypeSelected(selectedViewType);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">Select Review View</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Choose how you want to group and review your keywords. This will determine 
            how your data is organized in the evaluation page.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {availableViewTypes.map((viewType) => (
            <button
              key={viewType}
              onClick={() => setSelectedViewType(viewType)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedViewType === viewType
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 mt-1 mr-3 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedViewType === viewType
                    ? 'border-accent-500 bg-accent-500'
                    : 'border-gray-300'
                }`}>
                  {selectedViewType === viewType && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-primary-900 mb-1">
                    {viewTypeLabels[viewType].label}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {viewTypeLabels[viewType].description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedViewType}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedViewType
                ? 'bg-accent-500 text-white hover:bg-accent-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewTypeSelectionModal;
